/**
 * Locale model, detection, and persistence — the non-i18next-specific half of
 * the i18n layer. Pure + testable; no React, no i18next import.
 *
 * Detection order at boot (see ./index.ts initI18nLocale):
 *   1. explicit user choice persisted in @capacitor/preferences
 *   2. device locale (navigator.language), mapped to the nearest supported tag
 *   3. DEFAULT_LOCALE ('en')
 */
import { Preferences } from '@capacitor/preferences';
import { logger } from '@/lib/logger';

export const SUPPORTED_LOCALES = ['en', 'es-419', 'pt-BR', 'fr'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

/** Language names shown in their OWN language (autonyms). Like proper nouns,
 * these are NEVER translated — a speaker always recognizes their language in
 * the list regardless of the current UI locale. */
export const LOCALE_AUTONYMS: Record<Locale, string> = {
  en: 'English',
  'es-419': 'Español',
  'pt-BR': 'Português',
  fr: 'Français',
};

/** Compact code for the header pill. */
export const LOCALE_SHORT: Record<Locale, string> = {
  en: 'EN',
  'es-419': 'ES',
  'pt-BR': 'PT',
  fr: 'FR',
};

/** Dev-only QA locale (accented + padded en) to surface un-extracted strings
 * and layout overflow. Registered only when enabled — never in prod. */
export const PSEUDO_LOCALE = 'en-XA';

export const LOCALE_KEY = 'nynjwc.locale';

/** Exact match against a supported tag — used to accept a persisted user
 * override verbatim (it was chosen from the supported set). */
export function isSupportedLocale(value: string | null | undefined): value is Locale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Map any BCP-47 tag (e.g. 'es-MX', 'pt-PT', 'fr-CA', 'en-US') to the nearest
 * supported locale by primary subtag. Favors LatAm Spanish + Brazilian
 * Portuguese per SST-23. Unknown languages → DEFAULT_LOCALE. */
export function mapToSupported(raw: string | null | undefined): Locale {
  if (!raw) return DEFAULT_LOCALE;
  const primary = raw.toLowerCase().split('-')[0];
  switch (primary) {
    case 'es':
      return 'es-419';
    case 'pt':
      return 'pt-BR';
    case 'fr':
      return 'fr';
    case 'en':
      return 'en';
    default:
      return DEFAULT_LOCALE;
  }
}

function deviceLocale(): string | null {
  if (typeof navigator === 'undefined') return null;
  return navigator.language || navigator.languages?.[0] || null;
}

async function readStoredLocale(): Promise<string | null> {
  try {
    const { value } = await Preferences.get({ key: LOCALE_KEY });
    return value ?? null;
  } catch (err) {
    logger.warn('locale preference read failed', { err: String(err) });
    return null;
  }
}

/** Persist an explicit user choice. Best-effort — a write failure must not
 * break language switching (the in-memory change still applies). */
export async function persistLocale(locale: Locale): Promise<void> {
  try {
    await Preferences.set({ key: LOCALE_KEY, value: locale });
  } catch (err) {
    logger.warn('locale preference write failed', { err: String(err) });
  }
}

/** Resolve the locale to boot in: stored override → device → default. */
export async function detectInitialLocale(): Promise<Locale> {
  const stored = await readStoredLocale();
  if (isSupportedLocale(stored)) return stored;
  return mapToSupported(deviceLocale());
}

// ── Pseudo-locale (dev QA) ────────────────────────────────────────────────

const ACCENTS: Record<string, string> = {
  a: 'á', b: 'ƀ', c: 'ç', d: 'ð', e: 'é', f: 'ƒ', g: 'ĝ', h: 'ĥ', i: 'í',
  j: 'ĵ', k: 'ķ', l: 'ļ', m: 'ɱ', n: 'ñ', o: 'ó', p: 'þ', q: 'ɋ', r: 'ŕ',
  s: 'š', t: 'ţ', u: 'ú', v: 'ṽ', w: 'ŵ', x: 'χ', y: 'ý', z: 'ž',
  A: 'Á', B: 'Ɓ', C: 'Ç', D: 'Ð', E: 'É', F: 'Ƒ', G: 'Ĝ', H: 'Ĥ', I: 'Í',
  J: 'Ĵ', K: 'Ķ', L: 'Ļ', M: 'Ṁ', N: 'Ñ', O: 'Ó', P: 'Þ', Q: 'Ɋ', R: 'Ŕ',
  S: 'Š', T: 'Ţ', U: 'Ú', V: 'Ṽ', W: 'Ŵ', X: 'Χ', Y: 'Ý', Z: 'Ž',
};

/**
 * Transform an en string into its pseudo form: accent the letters, wrap in
 * brackets (to catch truncation), and pad ~30% (to catch overflow). `{{...}}`
 * interpolation tokens are preserved verbatim so the rendered value still
 * resolves names/counts.
 */
export function pseudoize(value: string): string {
  const accented = value.replace(/(\{\{[^}]+\}\})|[A-Za-z]/g, (match, interp) =>
    interp ? interp : (ACCENTS[match] ?? match),
  );
  const pad = '·'.repeat(Math.max(1, Math.ceil(value.length * 0.3)));
  return `⟦${accented}${pad}⟧`;
}
